import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryState, useQueryStates, parseAsInteger, parseAsString, parseAsStringLiteral } from 'nuqs';
import type { inferParserType } from 'nuqs';
import { useStore } from '@nanostores/react';
import { servicesQuery } from '@/services/api';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { authStore } from '@/services/auth/store';
import { useRoleDefinitions } from '@/features/roles/hook';
import { useListWorkspaceObjectTypes } from '@/features/workspaces/hooks';
import { isEmptyString } from '@/utils/string';
import { validateFiltersForNonPremium } from '../components/audit-logs-table/filter-utils';
import { getAuditLogDayLimit } from '../utils/access';
import type { AuditLogFilters, AuditLogResponse, AuditLogUser } from '../types';
import { AUDIT_LOGS_PAGE_SIZE } from '../constants/pagination';

interface UseAuditLogsDataOptions {
  context: 'account' | 'workspace';
  contextId: number | null | undefined;
}

const filtersParsers = {
  user_id: parseAsInteger,
  ip_addr: parseAsString,
  object_type: parseAsString,
  object_id: parseAsString,
  attribute_name: parseAsString,
  action: parseAsString,
  start_time: parseAsString,
  end_time: parseAsString,
  sort_order: parseAsStringLiteral(['asc', 'desc']).withDefault('desc'),
  campaign_id: parseAsInteger,
};

type UrlFilters = inferParserType<typeof filtersParsers>;

export function useAuditLogsData({ context, contextId }: UseAuditLogsDataOptions) {
  const auth = useAuth();
  const authData = useStore(authStore);
  const { track } = useAnalytics();

  const [urlFilters, setUrlFilters] = useQueryStates(filtersParsers, {
    // Use 'replace' to avoid polluting browser history with every filter change
    history: 'replace',
  });
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const filters = useMemo(() => {
    const updatedFilters: Partial<AuditLogFilters> = {};

    // Map `null` (used by nuqs for empty values) to `undefined` (used by our code)
    for (const [key, value] of Object.entries(urlFilters)) {
      const filterKey = key as keyof AuditLogFilters;
      (updatedFilters as Partial<AuditLogFilters>)[filterKey] = (value === null ? undefined : value) as never;
    }

    return {
      ...updatedFilters,
      cursor,
      limit: AUDIT_LOGS_PAGE_SIZE,
    } as AuditLogFilters;
  }, [urlFilters, cursor]);

  const setFilters = useCallback(
    (newFilters: AuditLogFilters) => {
      const { limit: _limit, cursor, ...rest } = newFilters;
      const updatedRest: Partial<UrlFilters> = {};

      // Map `undefined` (used by our code) to `null` values (used by nuqs for empty values)
      for (const [key, value] of Object.entries(rest)) {
        const filterKey = key as keyof UrlFilters;
        (updatedRest as Partial<UrlFilters>)[filterKey] = (value === undefined ? null : value) as never;
      }

      setUrlFilters(updatedRest);
      setCursor(cursor);
    },
    [setUrlFilters, setCursor],
  );

  const accountId = auth.account.id;
  const dayLimit = getAuditLogDayLimit(auth.account);

  // Get workspaces from auth store (available at account level for workspace filtering)
  const workspaces = useMemo(() => {
    return (
      authData.workspaces?.map((w) => ({
        id: Number.parseInt(w.id, 10),
        name: w.name,
      })) ?? []
    );
  }, [authData.workspaces]);

  // Check window.location directly on mount to avoid race condition with searchParams
  // This determines if we're in deep-link mode BEFORE React hydration affects searchParams
  // Use useState with lazy initializer to capture value once, avoiding side effects in useMemo
  const [initialAuditLogId] = useState<number | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const idParam = urlParams.get('audit_log_id');
    if (!isEmptyString(idParam) && /^\d+$/.test(idParam)) {
      const parsed = Number.parseInt(idParam, 10);
      return !Number.isNaN(parsed) ? parsed : null;
    }
    return null;
  });

  const [accumulatedLogs, setAccumulatedLogs] = useState<AuditLogResponse | undefined>(undefined);
  const [hasRestoredFromStorage, setHasRestoredFromStorage] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [shouldLoadUsers, setShouldLoadUsers] = useState(false);
  const [hasUsedInitialAuditLogId, setHasUsedInitialAuditLogId] = useState(false);

  // Search state for paginated filter dropdowns
  const [campaignSearchString, setCampaignSearchString] = useState<string>('');
  const [newsletterSearchString, setNewsletterSearchString] = useState<string>('');
  const [transactionalMessageSearchString, setTransactionalMessageSearchString] = useState<string>('');

  // Get audit log ID from URL for deep linking
  // Use initialAuditLogId on first render to avoid race condition, then sync with searchParams
  // Mark consumption via state update in useEffect to avoid side effects in useMemo
  const [auditLogIdParam] = useQueryState('audit_log_id', parseAsString);
  const auditLogId = useMemo(() => {
    // On first render, use the initial value captured from window.location
    // This prevents the race condition where searchParams isn't populated yet
    if (!hasUsedInitialAuditLogId && initialAuditLogId != null) {
      return initialAuditLogId;
    }

    // After first use, rely on searchParams which should be synced by now
    if (!isEmptyString(auditLogIdParam) && /^\d+$/.test(auditLogIdParam)) {
      const parsed = Number.parseInt(auditLogIdParam, 10);
      return !Number.isNaN(parsed) ? parsed : null;
    }
    return null;
  }, [auditLogIdParam, initialAuditLogId, hasUsedInitialAuditLogId]);

  // Mark initial value as used after first consumption (moved out of useMemo to avoid side effects)
  useEffect(() => {
    if (!hasUsedInitialAuditLogId && initialAuditLogId != null && auditLogId === initialAuditLogId) {
      setHasUsedInitialAuditLogId(true);
    }
  }, [hasUsedInitialAuditLogId, initialAuditLogId, auditLogId]);

  // Only create queries if we have the required data
  const shouldCreateQuery = contextId != null && auditLogId == null;
  const shouldFetchLogById = contextId != null && auditLogId != null;

  // API version for pre-computed diffs - include in cache keys to ensure proper invalidation
  const API_VERSION = 2;

  // Validate filters before API calls to ensure accounts with limited access can't bypass day limit
  // This is a safety check even though filters should already be validated
  const validatedFilters = useMemo(() => {
    return validateFiltersForNonPremium(filters, dayLimit);
  }, [filters, dayLimit]);

  // Fetch audit logs based on context (using v2 API for pre-computed diffs)
  const accountAuditLogsQuery = servicesQuery.getAccountAuditLogs.useQuery({
    queryKey: ['accountAuditLogs', contextId, validatedFilters, API_VERSION],
    queryData: {
      params: { account_id: contextId ?? 0 },
      query: { ...validatedFilters, version: API_VERSION },
    },
    enabled: context === 'account' && shouldCreateQuery,
  });

  const workspaceAuditLogsQuery = servicesQuery.getWorkspaceAuditLogs.useQuery({
    queryKey: ['workspaceAuditLogs', contextId, validatedFilters, API_VERSION],
    queryData: {
      params: { environment_id: contextId ?? 0 },
      query: { ...validatedFilters, version: API_VERSION },
    },
    enabled: context === 'workspace' && shouldCreateQuery,
  });

  // Deep link queries (using v2 API for pre-computed diffs)
  const accountAuditLogByIdQuery = servicesQuery.getAccountAuditLogById.useQuery({
    queryKey: ['accountAuditLogById', contextId, auditLogId, API_VERSION],
    queryData: {
      params: { account_id: contextId ?? 0, id: auditLogId ?? 0 },
      query: { version: API_VERSION },
    },
    enabled: context === 'account' && shouldFetchLogById,
  });

  const workspaceAuditLogByIdQuery = servicesQuery.getWorkspaceAuditLogById.useQuery({
    queryKey: ['workspaceAuditLogById', contextId, auditLogId, API_VERSION],
    queryData: {
      params: { environment_id: contextId ?? 0, id: auditLogId ?? 0 },
      query: { version: API_VERSION },
    },
    enabled: context === 'workspace' && shouldFetchLogById,
  });

  // Fetch all users for the account to populate User dropdown filter (lazy loaded)
  const allUsersQuery = servicesQuery.getAllUsers.useQuery({
    queryKey: ['allUsers', accountId],
    queryData: {
      params: { account_id: accountId ?? 0 },
    },
    enabled: shouldLoadUsers && accountId != null,
  });

  // Note: segments are eagerly loaded since they're needed to display names in activity logs and expanded details
  // Campaigns, newsletters, and transactional messages now use infinite queries with pagination and search support

  // Fetch campaigns for the workspace using infinite query with pagination and search
  // Fetch both active and archived campaigns since audit logs may reference old campaigns
  const campaignsQueryActive = servicesQuery.getCampaigns.useInfiniteQuery({
    queryKey: ['campaigns', contextId, 'active', campaignSearchString],
    initialPageParam: 1,
    queryData: ({ pageParam }) => ({
      params: { environment_id: contextId ?? 0 },
      query: {
        archived: false,
        page: pageParam as number,
        ...(campaignSearchString !== '' ? { searchString: campaignSearchString } : {}),
      },
    }),
    getNextPageParam: (lastPage: {
      body: { meta?: { pagination?: { page: number; total: number; size: number } } };
    }) => {
      const pagination = lastPage.body.meta?.pagination;
      if (pagination == null) return undefined;
      const totalPages = Math.ceil(pagination.total / pagination.size);
      return pagination.page < totalPages ? pagination.page + 1 : undefined;
    },
    enabled: context === 'workspace' && contextId != null,
  });

  const campaignsQueryArchived = servicesQuery.getCampaigns.useInfiniteQuery({
    queryKey: ['campaigns', contextId, 'archived', campaignSearchString],
    initialPageParam: 1,
    queryData: ({ pageParam }) => ({
      params: { environment_id: contextId ?? 0 },
      query: {
        archived: true,
        page: pageParam as number,
        ...(campaignSearchString !== '' ? { searchString: campaignSearchString } : {}),
      },
    }),
    getNextPageParam: (lastPage: {
      body: { meta?: { pagination?: { page: number; total: number; size: number } } };
    }) => {
      const pagination = lastPage.body.meta?.pagination;
      if (pagination == null) return undefined;
      const totalPages = Math.ceil(pagination.total / pagination.size);
      return pagination.page < totalPages ? pagination.page + 1 : undefined;
    },
    enabled: context === 'workspace' && contextId != null,
  });

  // Combine active and archived campaigns from all pages into a single array
  // This ensures we can look up campaign names even for archived campaigns referenced in audit logs
  const allCampaigns = useMemo(() => {
    const active = campaignsQueryActive.data?.pages.flatMap((page) => page.body.campaigns ?? []) ?? [];
    const archived = campaignsQueryArchived.data?.pages.flatMap((page) => page.body.campaigns ?? []) ?? [];
    return [...active, ...archived];
  }, [campaignsQueryActive.data, campaignsQueryArchived.data]);

  // Create a combined query object that merges both active and archived campaigns
  // Use the structure from active query as the base, but replace campaigns array with combined list
  const campaignsQuery = useMemo(() => {
    const baseQuery = campaignsQueryActive;
    return {
      ...baseQuery,
      data:
        campaignsQueryActive.data != null || campaignsQueryArchived.data != null
          ? {
              pages: [
                {
                  body: {
                    campaigns: allCampaigns,
                  },
                },
              ],
            }
          : undefined,
      isLoading: campaignsQueryActive.isLoading || campaignsQueryArchived.isLoading,
      isFetchingNextPage: campaignsQueryActive.isFetchingNextPage || campaignsQueryArchived.isFetchingNextPage,
      hasNextPage: campaignsQueryActive.hasNextPage === true || campaignsQueryArchived.hasNextPage === true,
      fetchNextPage: async () => {
        // Only fetch next page for queries that have more pages and aren't already fetching
        const promises: Promise<unknown>[] = [];
        if (campaignsQueryActive.hasNextPage && !campaignsQueryActive.isFetchingNextPage) {
          promises.push(campaignsQueryActive.fetchNextPage());
        }
        if (campaignsQueryArchived.hasNextPage && !campaignsQueryArchived.isFetchingNextPage) {
          promises.push(campaignsQueryArchived.fetchNextPage());
        }
        await Promise.all(promises);
      },
      error: campaignsQueryActive.error ?? campaignsQueryArchived.error,
    };
  }, [campaignsQueryActive, campaignsQueryArchived, allCampaigns]);

  // Fetch segments for the workspace (eagerly loaded since they're needed for displaying
  // segment names in conditions, triggers, and filters within expanded details)
  const segmentsQuery = servicesQuery.getSegments.useQuery({
    queryKey: ['segments', contextId],
    queryData: {
      params: { environment_id: contextId ?? 0 },
    },
    enabled: context === 'workspace' && contextId != null,
  });

  // Fetch newsletters for the workspace using infinite query with pagination and search
  const newslettersQuery = servicesQuery.getNewsletters.useInfiniteQuery({
    queryKey: ['newsletters', contextId, newsletterSearchString],
    initialPageParam: 1,
    queryData: ({ pageParam }) => ({
      params: { environment_id: contextId ?? 0 },
      query: {
        page: pageParam as number,
        ...(newsletterSearchString !== '' ? { searchString: newsletterSearchString } : {}),
      },
    }),
    getNextPageParam: (lastPage: {
      body: { meta?: { pagination?: { page: number; total: number; size: number } } };
    }) => {
      const pagination = lastPage.body.meta?.pagination;
      if (pagination == null) return undefined;
      const totalPages = Math.ceil(pagination.total / pagination.size);
      return pagination.page < totalPages ? pagination.page + 1 : undefined;
    },
    enabled: context === 'workspace' && contextId != null,
  });

  // Fetch transactional messages for the workspace with search support
  // Note: Transactional messages API may not support pagination, so we use regular query with search
  const transactionalMessagesQuery = servicesQuery.getTransactionalMessages.useQuery({
    queryKey: ['transactionalMessages', contextId, transactionalMessageSearchString],
    queryData: {
      params: { environment_id: contextId ?? 0 },
      query: transactionalMessageSearchString !== '' ? { searchString: transactionalMessageSearchString } : {},
    },
    enabled: context === 'workspace' && contextId != null,
  });

  // Fetch delivery filters (messages/campaign actions) for the workspace
  // This eagerly loads messages to display message names (e.g., "Email 1" instead of "output_5") in conditions
  const deliveryFiltersQuery = servicesQuery.getDeliveryFilters.useQuery({
    queryKey: ['deliveryFilters', contextId],
    queryData: {
      params: { environment_id: contextId ?? 0 },
    },
    enabled: context === 'workspace' && contextId != null,
  });

  // Transform delivery filters response to MessageInfo format
  const messages = useMemo(() => {
    // Handle errors gracefully - if the endpoint doesn't exist yet, return empty array
    if (deliveryFiltersQuery.error != null || deliveryFiltersQuery.data == null) {
      return [];
    }

    const filtersData = deliveryFiltersQuery.data.body;
    if (filtersData == null || typeof filtersData !== 'object') {
      return [];
    }

    // Use segmentable messages (filtered list) or fall back to all messages
    const messagesList = filtersData.segmentable ?? filtersData.messages ?? [];
    if (!Array.isArray(messagesList)) {
      return [];
    }

    return messagesList
      .map((msg) => {
        if (msg == null || typeof msg !== 'object') {
          return null;
        }
        // Cast to access optional name field not in type definition
        const msgWithName = msg as typeof msg & { name?: string };
        return {
          output_id: String(msg.output_id ?? ''),
          subject: msg.subject as string | undefined,
          name: (msg.subject as string | undefined) ?? msgWithName.name,
          type: msg.type as string | undefined,
          campaign_id:
            msg.campaign_id != null && msg.campaign_id !== ''
              ? Number.parseInt(String(msg.campaign_id), 10)
              : undefined,
        };
      })
      .filter((msg): msg is NonNullable<typeof msg> => msg != null);
  }, [deliveryFiltersQuery.data, deliveryFiltersQuery.error]);

  // Fetch object types for the workspace (eagerly loaded for relationship conditions)
  const objectTypesQuery = useListWorkspaceObjectTypes(context === 'workspace' ? contextId ?? undefined : undefined);

  const auditLogsQuery = context === 'account' ? accountAuditLogsQuery : workspaceAuditLogsQuery;
  const singleLogQuery = context === 'account' ? accountAuditLogByIdQuery : workspaceAuditLogByIdQuery;

  // Restore accumulated logs from sessionStorage on mount
  useEffect(() => {
    if (!hasRestoredFromStorage && contextId != null) {
      try {
        const key = `audit-logs-${context}-${contextId}`;
        const stored = sessionStorage.getItem(key);
        if (!isEmptyString(stored)) {
          const parsedData = JSON.parse(stored);
          setAccumulatedLogs(parsedData);
        }
      } catch (error) {
        // Silently fail if restoration fails
      }
      setHasRestoredFromStorage(true);
      setIsInitialMount(false);
    }
  }, [contextId, hasRestoredFromStorage, context]);

  // Accumulate logs when "Load More" is clicked
  useEffect(() => {
    if (auditLogsQuery?.data != null) {
      const newData = auditLogsQuery.data.body;

      if (!isEmptyString(filters.cursor)) {
        setAccumulatedLogs((prev) => {
          if (prev == null) return newData;

          // Deduplicate audit logs
          const logsMap = new Map<number, (typeof newData.data)[0]>();
          for (const log of [...(prev.data ?? []), ...(newData.data ?? [])]) {
            logsMap.set(log.id, log);
          }

          // Deduplicate users
          const usersMap = new Map<number, AuditLogUser>();
          for (const user of [...(prev.users ?? []), ...(newData.users ?? [])]) {
            usersMap.set(user.user_id, user);
          }

          return {
            data: Array.from(logsMap.values()),
            users: Array.from(usersMap.values()),
            pagination: newData.pagination,
          };
        });
      } else {
        setAccumulatedLogs(newData);
      }
    }
  }, [auditLogsQuery.data, filters.cursor]);

  // Save accumulated logs to sessionStorage
  useEffect(() => {
    if (accumulatedLogs != null && contextId != null) {
      try {
        const key = `audit-logs-${context}-${contextId}`;
        sessionStorage.setItem(key, JSON.stringify(accumulatedLogs));
      } catch (error) {
        // Ignore storage errors
      }
    }
  }, [accumulatedLogs, contextId, context]);

  // Reset accumulated logs when filters change
  useEffect(() => {
    if (isInitialMount) {
      return;
    }

    // Only reset if we're not paginating
    if (isEmptyString(filters.cursor)) {
      setAccumulatedLogs(undefined);
      setHasRestoredFromStorage(false);
      if (contextId != null) {
        try {
          const key = `audit-logs-${context}-${contextId}`;
          sessionStorage.removeItem(key);
        } catch (error) {
          // Ignore errors
        }
      }
    }
  }, [
    filters.user_id,
    filters.ip_addr,
    filters.object_type,
    filters.object_id,
    filters.attribute_name,
    filters.action,
    filters.campaign_id,
    filters.start_time,
    filters.end_time,
    filters.sort_order,
    filters.cursor,
    isInitialMount,
    contextId,
    context,
  ]);

  const handleFiltersChange = useCallback(
    (newFilters: Partial<AuditLogFilters>) => {
      // If only the cursor is being set (Load More), don't reset it
      // Otherwise, reset cursor when filter values change
      if (Object.keys(newFilters).length === 1 && 'cursor' in newFilters) {
        setFilters(newFilters as AuditLogFilters);
      } else {
        setFilters({ ...newFilters, cursor: undefined } as AuditLogFilters);
      }
      track('auditLogs', 'filtersChanged', {
        filterCount: Object.keys(newFilters).length,
      });
    },
    [setFilters, track],
  );

  const handleLoadUsers = useCallback(() => {
    setShouldLoadUsers(true);
  }, []);

  const handleLoadSegments = useCallback(() => {
    // Segments are eagerly loaded, so this is a no-op but kept for API consistency
  }, []);

  // Handlers for loading more pages (for infinite queries)
  const handleLoadMoreCampaigns = useCallback(() => {
    if (campaignsQuery.hasNextPage && !campaignsQuery.isFetchingNextPage) {
      campaignsQuery.fetchNextPage();
    }
  }, [campaignsQuery]);

  const handleLoadMoreNewsletters = useCallback(() => {
    if (newslettersQuery.hasNextPage && !newslettersQuery.isFetchingNextPage) {
      newslettersQuery.fetchNextPage();
    }
  }, [newslettersQuery]);

  // Handlers for search (these will reset the queries with new search strings)
  // When search changes, the query key changes which automatically resets the query
  const handleCampaignSearch = useCallback((searchString: string) => {
    setCampaignSearchString(searchString);
  }, []);

  const handleNewsletterSearch = useCallback((searchString: string) => {
    setNewsletterSearchString(searchString);
  }, []);

  const handleTransactionalMessageSearch = useCallback((searchString: string) => {
    setTransactionalMessageSearchString(searchString);
  }, []);

  // Fetch role definitions for mapping role IDs to friendly names
  const roleDefinitions = useRoleDefinitions();

  // Flatten newsletters from pages for use in Select options
  const allNewsletters = useMemo(() => {
    return newslettersQuery.data?.pages.flatMap((page) => page.body.newsletters ?? []) ?? [];
  }, [newslettersQuery.data]);

  // Flatten transactional messages (they're not paginated, but keep consistent structure)
  const allTransactionalMessages = useMemo(() => {
    return transactionalMessagesQuery.data?.body?.transactional_messages ?? [];
  }, [transactionalMessagesQuery.data]);

  return {
    filters,
    auditLogId,
    accumulatedLogs,
    auditLogsQuery,
    singleLogQuery,
    allUsersQuery,
    campaignsQuery,
    segmentsQuery,
    newslettersQuery,
    transactionalMessagesQuery,
    objectTypesQuery,
    workspaces,
    handleFiltersChange,
    handleLoadUsers,
    handleLoadSegments,
    // Campaign handlers
    campaigns: allCampaigns,
    isLoadingCampaigns: campaignsQuery.isLoading,
    isFetchingMoreCampaigns: campaignsQuery.isFetchingNextPage,
    hasMoreCampaigns: campaignsQuery.hasNextPage,
    onLoadMoreCampaigns: handleLoadMoreCampaigns,
    onCampaignSearch: handleCampaignSearch,
    campaignSearchString,
    // Newsletter handlers
    newsletters: allNewsletters,
    isLoadingNewsletters: newslettersQuery.isLoading,
    isFetchingMoreNewsletters: newslettersQuery.isFetchingNextPage,
    hasMoreNewsletters: newslettersQuery.hasNextPage,
    onLoadMoreNewsletters: handleLoadMoreNewsletters,
    onNewsletterSearch: handleNewsletterSearch,
    newsletterSearchString,
    // Transactional message handlers
    transactionalMessages: allTransactionalMessages,
    isLoadingTransactionalMessages: transactionalMessagesQuery.isLoading,
    onTransactionalMessageSearch: handleTransactionalMessageSearch,
    transactionalMessageSearchString,
    // Legacy props for backward compatibility
    isLoadingUsers: allUsersQuery.isLoading,
    isLoadingSegments: segmentsQuery.isLoading,
    messages,
    isLoadingMessages: deliveryFiltersQuery.isLoading,
    onLoadMessages: () => {
      // Messages are eagerly loaded, so this is a no-op but kept for API consistency
    },
    roleDefinitions,
  };
}
